import React, { FC, useState, useEffect, useRef } from 'react';
import { Page } from '@src/views/containers';
import { ChatMessage, useAiContext } from '@src/views/pages/projects/dash/AiContext';
import {
  AIContextPills,
} from '@src/views/pages/projects/dash/DashAIPanel';
import AIChatBox from '@src/views/pages/projects/dash/AIChatBox';
import AIObjectPicker, { SelectedItem } from '@src/views/pages/projects/dash/AIObjectPicker';
import { CorvalProjectAccount, CorvalProjectVersion, SemanticUIColor } from '@src/types';
import { Icon, Button, Header, Menu, Modal } from 'semantic-ui-react';
import { useCurrentUser, usePrefStates } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';

// @ts-ignore
import sparkleSvg from '@src/_assets/images/sparkle.svg';
import '../projects/dash/Dash.scss';

const SCOPED_AI_OBJECT_TYPES: SelectedItem['objectType'][] = ['Project_Id', 'Project_Version_Id'];

interface ApiConversationMessage {
  id: string;
  sequenceNumber?: number;
  userPrompt: string;
  aiResponse?: string;
  createdUtc: string;
}

interface ApiConversation {
  conversationId: string;
  threadId?: string;
  initialQuestion: string;
  indexes?: string[];
  variables?: Record<string, string[]>;
  datasetJson?: string;
  reasoning?: string;
  createdUtc: string;
  updatedUtc: string;
  messages?: ApiConversationMessage[];
}

interface DatasetPayload {
  indexName: string;
  documents: any[];
}

interface ConversationTab {
  id: string;
  agentKey: string;
  title: string;
  threadId: string | null;
  indexes: string[];
  variables: Record<string, string[]>;
  datasetPayload: DatasetPayload[];
  updatedUtc: string;
  isTemporary?: boolean;
}

const sanitizeAssistantMessageText = (text: string): string => {
  const lines = text.split('\n');
  const kept: string[] = [];
  let skippingMetadataBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const startsMetadataBlock = /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

    if (startsMetadataBlock) {
      skippingMetadataBlock = true;
      continue;
    }

    if (skippingMetadataBlock) {
      const isContinuation =
        trimmed.length === 0 ||
        /^[-*]/.test(trimmed) ||
        /^[\[{\"]/.test(trimmed) ||
        /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

      if (isContinuation) {
        continue;
      }

      skippingMetadataBlock = false;
    }

    kept.push(line);
  }

  return kept.join('\n').trimEnd();
};

function isRichAiContextItems(items: SelectedItem[]): boolean {
  const allowed = new Set<string>(SCOPED_AI_OBJECT_TYPES);
  return items.some((i) => !allowed.has(i.objectType));
}

function areAiSelectionsEqualByIds(a: SelectedItem[], b: SelectedItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].objectType !== b[i].objectType || a[i].objectId !== b[i].objectId) {
      return false;
    }
  }
  return true;
}

function normalizePrefProjectId(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '' || raw === 0) {
    return '';
  }
  const str = String(raw).trim();
  if (str === '0') {
    return '';
  }
  return str;
}

function findVersionLabel(projectAccount: CorvalProjectAccount, versionId: string): string {
  const versions = (projectAccount.project?.projectVersions ?? []).filter(Boolean) as CorvalProjectVersion[];
  const match = versions.find((v) => v.id === versionId);
  return match?.name?.trim() ? match.name : 'Version';
}

function normalizePrefVersionId(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  return String(raw);
}

function buildProjectScopeItems(account: CorvalProjectAccount, versionId: string): SelectedItem[] {
  const projectId = account.projectId;
  const projectName = account.project?.name?.trim() ? account.project.name : 'Project';
  const scopeItems: SelectedItem[] = [
    { objectType: 'Project_Id', objectId: projectId, name: projectName },
  ];

  if (versionId) {
    scopeItems.push({
      objectType: 'Project_Version_Id',
      objectId: versionId,
      name: findVersionLabel(account, versionId),
    });
  }

  return scopeItems;
}

function mergeProjectScopeIntoSelection(prev: SelectedItem[], scopeItems: SelectedItem[]): SelectedItem[] {
  if (!isRichAiContextItems(prev)) {
    return scopeItems;
  }

  const withoutScope = prev.filter(
    (item) => item.objectType !== 'Project_Id' && item.objectType !== 'Project_Version_Id'
  );

  return [...scopeItems, ...withoutScope];
}

function areProjectScopeItemsEqual(a: SelectedItem[], b: SelectedItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < b.length; i += 1) {
    const match = a.find((item) => item.objectType === b[i].objectType && item.objectId === b[i].objectId);
    if (!match || match.name !== b[i].name) {
      return false;
    }
  }

  return true;
}

const DoraAIAssistantPageContent: FC = () => {
  const {
    selectedObjects = [],
    setSelectedObjects,
    updateAgent,
    getAgent,
  } = useAiContext();
  const { GetAIConversations } = useCentralCalls();
  const { currentUser } = useCurrentUser();
  const [, prefs, prefEnums] = usePrefStates();
  const prefProjectIdKey = normalizePrefProjectId(prefs.get(prefEnums.PROJECT_ID));
  const [, projectSpecificPrefs] = usePrefStates(prefProjectIdKey);
  const projects = currentUser?.projects;
  const [conversationTabs, setConversationTabs] = useState<ConversationTab[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [datasetModalOpen, setDatasetModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const lastSyncedScopeRef = useRef<string | null>(null);
  const [prefsRevision, setPrefsRevision] = useState(0);

  const prefProjectVersionId = normalizePrefVersionId(
    projectSpecificPrefs.get(prefEnums.PROJECT_VERSION_ID)
  );
  const scopeSyncKey = `${prefProjectIdKey}|${prefProjectVersionId}`;

  useEffect(() => {
    const handlePrefsUpdated = () => setPrefsRevision((revision) => revision + 1);

    window.addEventListener('storage', handlePrefsUpdated);

    return () => window.removeEventListener('storage', handlePrefsUpdated);
  }, []);

  useEffect(() => {
    if (!projects) {
      return;
    }

    if (lastSyncedScopeRef.current === scopeSyncKey) {
      return;
    }

    const account = projects.find((p) => p.projectId === prefProjectIdKey);

    if (!account?.project?.id) {
      lastSyncedScopeRef.current = scopeSyncKey;

      setSelectedObjects((prev) => {
        if (isRichAiContextItems(prev)) {
          return prev;
        }
        return prev.length > 0 ? [] : prev;
      });

      return;
    }

    const scopeItems = buildProjectScopeItems(account, prefProjectVersionId);

    setSelectedObjects((prev) => {
      const next = mergeProjectScopeIntoSelection(prev, scopeItems);
      const prevScope = prev.filter(
        (item) => item.objectType === 'Project_Id' || item.objectType === 'Project_Version_Id'
      );

      if (areAiSelectionsEqualByIds(prev, next) && areProjectScopeItemsEqual(prevScope, scopeItems)) {
        return prev;
      }

      return next;
    });

    lastSyncedScopeRef.current = scopeSyncKey;
  }, [prefProjectIdKey, prefProjectVersionId, projects, prefsRevision, scopeSyncKey, setSelectedObjects]);

  const buildDatasetMessageText = (conversation: ApiConversation): string => {
    const indexes = Array.isArray(conversation.indexes) ? conversation.indexes : [];
    const variables = conversation.variables ?? {};
    const variablePairs = Object.entries(variables);

    const lines: string[] = ['### Dataset'];
    lines.push(indexes.length > 0 ? `**Indexes:** ${indexes.join(', ')}` : '**Indexes:** None');

    if (variablePairs.length > 0) {
      lines.push('**Variables:**');
      variablePairs.forEach(([key, values]) => {
        lines.push(`- ${key}: ${(values ?? []).join(', ')}`);
      });
    } else {
      lines.push('**Variables:** None');
    }

    return lines.join('\n');
  };

  const buildMessagesForConversation = (conversation: ApiConversation): ChatMessage[] => {
    const baseTimestamp = new Date(conversation.createdUtc || conversation.updatedUtc || Date.now());
    const messages: ChatMessage[] = [
      {
        id: `dataset-${conversation.conversationId}`,
        text: buildDatasetMessageText(conversation),
        isUser: false,
        timestamp: baseTimestamp,
      },
    ];

    const turns = Array.isArray(conversation.messages) ? conversation.messages : [];
    turns.forEach((turn, idx) => {
      const turnTimestamp = new Date(turn.createdUtc || baseTimestamp);
      if (turn.userPrompt && turn.userPrompt.trim()) {
        messages.push({
          id: `user-${conversation.conversationId}-${turn.id || idx}`,
          text: turn.userPrompt,
          isUser: true,
          timestamp: turnTimestamp,
        });
      }

      if (turn.aiResponse && turn.aiResponse.trim()) {
        messages.push({
          id: `assistant-${conversation.conversationId}-${turn.id || idx}`,
          text: sanitizeAssistantMessageText(turn.aiResponse),
          isUser: false,
          timestamp: turnTimestamp,
          debug: {
            source: 'historical-conversation',
            conversationId: conversation.conversationId,
            threadId: conversation.threadId ?? null,
            sequenceNumber: turn.sequenceNumber ?? null,
            createdUtc: turn.createdUtc,
            indexes: Array.isArray(conversation.indexes) ? conversation.indexes : [],
            variables: conversation.variables ?? {},
            reasoning: conversation.reasoning ?? null,
          },
        });
      }
    });

    return messages;
  };

  const getConversationTitle = (conversation: ApiConversation): string => {
    const question = conversation.initialQuestion?.trim() ?? '';
    if (question.length === 0) {
      return 'Conversation';
    }

    return question.length > 48 ? `${question.slice(0, 48)}...` : question;
  };

  const createTemporaryConversationTab = (): ConversationTab => {
    const id = `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const agentKey = `conversation:${id}`;
    updateAgent(agentKey, {
      messages: [],
      threadId: null,
      isLoading: false,
    });

    return {
      id,
      agentKey,
      title: 'New Conversation',
      threadId: null,
      indexes: [],
      variables: {},
      datasetPayload: [],
      updatedUtc: new Date().toISOString(),
      isTemporary: true,
    };
  };

  const parseDatasetPayload = (datasetJson?: string): DatasetPayload[] => {
    if (!datasetJson || !datasetJson.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(datasetJson);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item: any) => {
          const indexName =
            (typeof item?.IndexName === 'string' && item.IndexName.trim()) ||
            (typeof item?.indexName === 'string' && item.indexName.trim()) ||
            'index';

          const docs = Array.isArray(item?.Documents)
            ? item.Documents
            : Array.isArray(item?.documents)
              ? item.documents
              : [];

          return {
            indexName,
            documents: docs,
          } as DatasetPayload;
        })
        .filter((payload: DatasetPayload) => payload.documents.length > 0);
    } catch {
      return [];
    }
  };

  const renderJsonValue = (value: any, depth: number = 0, label?: string): React.ReactNode => {
    const indent = depth * 12;
    const summaryStyle: React.CSSProperties = {
      cursor: 'pointer',
      fontWeight: 700,
      background: '#f6f2fb',
      border: '1px solid #e6d9f5',
      padding: '4px 8px',
      borderRadius: 6,
      display: 'inline-block'
    };
    const contentStyle: React.CSSProperties = {
      marginTop: 6,
      marginLeft: 12,
      paddingLeft: 8,
      borderLeft: '2px solid #e6d9f5'
    };

    if (value === null || value === undefined) {
      return (
        <div style={{ marginLeft: indent, color: '#888' }}>
          {label ? <span style={{ fontWeight: 700 }}>{label}:</span> : null}{' '}
          {String(value)}
        </div>
      );
    }

    if (Array.isArray(value)) {
      const header = label ? `${label} [${value.length}]` : `Array [${value.length}]`;
      return (
        <div style={{ marginLeft: indent, marginBottom: 8 }}>
          <details>
            <summary style={summaryStyle}>{header}</summary>
            <div style={contentStyle}>
              {value.map((item, index) => (
                <div key={`arr-${depth}-${index}`} style={{ marginBottom: 6 }}>
                  {renderJsonValue(item, depth + 1, `[${index}]`)}
                </div>
              ))}
            </div>
          </details>
        </div>
      );
    }

    if (typeof value === 'object') {
      const header = label ?? 'Object';
      return (
        <div style={{ marginLeft: indent, marginBottom: 8 }}>
          <details>
            <summary style={summaryStyle}>{header}</summary>
            <div style={contentStyle}>
              {Object.entries(value).map(([key, val]) => (
                <div key={`obj-${depth}-${key}`} style={{ marginBottom: 6 }}>
                  {renderJsonValue(val, depth + 1, key)}
                </div>
              ))}
            </div>
          </details>
        </div>
      );
    }

    return (
      <div style={{ marginLeft: indent, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {label ? <span style={{ fontWeight: 700 }}>{label}:</span> : null}{' '}
        {String(value)}
      </div>
    );
  };

  const loadConversations = async (preferredConversationId?: string) => {
    setIsLoadingConversations(true);
    try {
      const data = (await GetAIConversations()) as ApiConversation[];
      const sorted = [...data].sort(
        (a, b) => new Date(b.updatedUtc).getTime() - new Date(a.updatedUtc).getTime()
      );

      const tabs: ConversationTab[] = sorted.map((conversation) => {
        const agentKey = `conversation:${conversation.conversationId}`;
        updateAgent(agentKey, {
          messages: buildMessagesForConversation(conversation),
          threadId: conversation.threadId ?? null,
          isLoading: false,
        });

        return {
          id: conversation.conversationId,
          agentKey,
          title: getConversationTitle(conversation),
          threadId: conversation.threadId ?? null,
          indexes: Array.isArray(conversation.indexes) ? conversation.indexes : [],
          variables: conversation.variables ?? {},
          datasetPayload: parseDatasetPayload(conversation.datasetJson),
          updatedUtc: conversation.updatedUtc,
        };
      });

      if (tabs.length === 0) {
        const newTab = createTemporaryConversationTab();
        setConversationTabs([newTab]);
        setActiveConversationId(newTab.id);
        return;
      }

      setConversationTabs(tabs);

      if (preferredConversationId && tabs.some((tab) => tab.id === preferredConversationId)) {
        setActiveConversationId(preferredConversationId);
      } else if (activeConversationId && tabs.some((tab) => tab.id === activeConversationId)) {
        setActiveConversationId(activeConversationId);
      } else {
        setActiveConversationId(tabs[0].id);
      }
    } catch (error) {
      console.error('Failed to load AI conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createConversationTab = () => {
    const newTab = createTemporaryConversationTab();
    setConversationTabs((prev) => [newTab, ...prev]);
    setActiveConversationId(newTab.id);
  };

  const handleAssistantMessage = async (msg: ChatMessage) => {
    if (!msg.conversationId) {
      return;
    }

    const applyRuntimeDebugToLatestAssistant = (conversationId: string, runtimeDebug: any) => {
      if (!runtimeDebug) {
        return;
      }

      const agentKey = `conversation:${conversationId}`;
      const state = getAgent(agentKey);
      const currentMessages = state?.messages ?? [];
      if (currentMessages.length === 0) {
        return;
      }

      const latestAssistantIndex = [...currentMessages]
        .map((message, index) => ({ message, index }))
        .reverse()
        .find((entry) => !entry.message.isUser && !entry.message.isHeader)?.index;

      if (latestAssistantIndex === undefined) {
        return;
      }

      const nextMessages = currentMessages.map((message, index) => {
        if (index !== latestAssistantIndex) {
          return message;
        }

        return {
          ...message,
          debug: {
            ...(message.debug ?? {}),
            ...runtimeDebug,
            source: 'historical+runtime',
          },
        };
      });

      updateAgent(agentKey, { messages: nextMessages });
    };

    const startedNewConversation = msg.debug?.startedNewConversation === true;
    const switchedConversationId =
      !!activeConversationId &&
      msg.conversationId !== activeConversationId;

    if (startedNewConversation || switchedConversationId) {
      await loadConversations(msg.conversationId);
      applyRuntimeDebugToLatestAssistant(msg.conversationId, msg.debug);
      return;
    }

    const activeTab = conversationTabs.find((tab) => tab.id === activeConversationId) ?? null;
    if (activeTab?.isTemporary) {
      await loadConversations(msg.conversationId);
      return;
    }

    setConversationTabs((prev) =>
      prev.map((tab) =>
        tab.id === msg.conversationId
          ? {
              ...tab,
              updatedUtc: new Date().toISOString(),
            }
          : tab
      )
    );
  };

  const highlightedTypes = new Set(selectedObjects.map((o) => o.objectType));
  const activeConversation = conversationTabs.find((tab) => tab.id === activeConversationId) ?? null;
  const datasetRecordCount = activeConversation?.datasetPayload.reduce((sum, payload) => sum + payload.documents.length, 0) ?? 0;

  const removeSelectedObject = (objectId: string) =>
    setSelectedObjects((prev) => prev.filter((o) => o.objectId !== objectId));

  const handlePickerClose = (selected: SelectedItem[]) => {
    setSelectedObjects(selected);
    setPickerOpen(false);
  };

  return (
    <Page
      showCorvalAssistant={false}
      padMainHorizontal={false}
      padMainVertical={false}
      allowContentScroll
      showToggleMode={false}
    >
      <div className="ai-assistant-page">

        {/* ── LEFT SIDEBAR ─────────────────────────────────────────────────── */}
        <div className="ai-assistant-sidebar">
          <Header as="h3" style={{ color: SemanticUIColor.PURPLE, marginBottom: 24, paddingLeft: 4 }}>
            <img src={sparkleSvg} alt="AI Assistant" style={{ width: 24, height: 24, marginRight: 12, verticalAlign: 'middle' }} />
            <Header.Content style={{ verticalAlign: 'middle' }}>
              AI Assistant
              <Header.Subheader>Your Project Assistant</Header.Subheader>
            </Header.Content>
          </Header>

          <Button
            fluid
            color="purple"
            icon="plus"
            content="New Conversation"
            onClick={createConversationTab}
            style={{ marginBottom: 12 }}
          />

          {isLoadingConversations && (
            <div style={{ fontSize: 12, color: '#777', marginBottom: 8 }}>
              Loading conversations...
            </div>
          )}

          <Menu
            vertical
            fluid
            secondary
            text
            style={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              paddingRight: 4,
            }}
          >
            {conversationTabs.map((tab) => (
              <Menu.Item
                key={tab.id}
                active={activeConversationId === tab.id}
                onClick={() => setActiveConversationId(tab.id)}
                style={{
                  padding: '12px 16px',
                  borderRadius: 8,
                  marginBottom: 4,
                  fontWeight: activeConversationId === tab.id ? 600 : 400,
                  backgroundColor: activeConversationId === tab.id ? '#F3EAFC' : 'transparent',
                  color: activeConversationId === tab.id ? SemanticUIColor.PURPLE : '#555',
                }}
              >
                <Icon name={tab.isTemporary ? 'edit outline' : 'comments outline'} style={{ marginRight: 12, opacity: activeConversationId === tab.id ? 1 : 0.7 }} />
                <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <span>{tab.title}</span>
                  <span style={{ fontSize: 11, opacity: 0.75 }}>
                    {new Date(tab.updatedUtc).toLocaleString()}
                  </span>
                </div>
              </Menu.Item>
            ))}
          </Menu>
        </div>

        {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
        <div className="ai-assistant-content">

          {/* Header bar */}
          <div className="ai-assistant-header-bar">
            <Header as="h4" style={{ margin: 0, color: '#333' }}>
              {activeConversation?.title ?? 'Conversation'}
              <Header.Subheader>
                {selectedObjects.length > 0
                  ? `Focusing on ${selectedObjects.length} item${selectedObjects.length !== 1 ? 's' : ''}`
                  : 'Select items for focused analysis'}
              </Header.Subheader>
            </Header>

            <Button
              size="tiny"
              basic
              icon="add"
              content="Select Objects"
              onClick={() => setPickerOpen(true)}
            />
          </div>

          {activeConversation && activeConversation.indexes.length > 0 && (
            <div style={{ padding: '0 12px 8px' }}>
              <Button
                basic
                size="small"
                icon="database"
                content={`Dataset: ${activeConversation.indexes.join(', ')} (${datasetRecordCount} records)`}
                onClick={() => setDatasetModalOpen(true)}
              />
            </div>
          )}

          {/* Context pills (only when objects selected) */}
          {selectedObjects.length > 0 && (
            <div className="ai-assistant-pills">
              <AIContextPills
                selectedObjects={selectedObjects}
                highlightedTypes={highlightedTypes}
                onRemove={removeSelectedObject}
              />
            </div>
          )}

          {/* Chat area */}
          <div className="ai-assistant-chat-area">
            {activeConversation && (
              <AIChatBox
                key={activeConversation.agentKey}
                agentType={activeConversation.agentKey}
                onAssistantMessage={handleAssistantMessage}
              />
            )}
          </div>

        </div>
      </div>

      <AIObjectPicker open={pickerOpen} onClose={handlePickerClose} />

      <Modal
        open={datasetModalOpen}
        onClose={() => setDatasetModalOpen(false)}
        size="large"
      >
        <Modal.Header>
          Dataset Viewer
        </Modal.Header>
        <Modal.Content scrolling>
          {!activeConversation || activeConversation.datasetPayload.length === 0 ? (
            <div style={{ color: '#666' }}>No dataset available for this conversation.</div>
          ) : (
            <div>
              {activeConversation.datasetPayload.map((payload, idx) => (
                <div key={`${payload.indexName}-${idx}`} style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>
                    {payload.indexName} ({payload.documents.length} records)
                  </div>
                  {payload.documents.map((doc, docIndex) => (
                    <div
                      key={`${payload.indexName}-${idx}-${docIndex}`}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #e8e8e8',
                        borderRadius: 8,
                        marginBottom: 10,
                        background: '#fff',
                      }}
                    >
                      {renderJsonValue(doc)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDatasetModalOpen(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    </Page>
  );
};

const DoraAIAssistantPage: FC = () => {
  return <DoraAIAssistantPageContent />;
};

export default DoraAIAssistantPage;
